import type {
    AutocompleteChangeReason,
    AutocompleteCloseReason,
    AutocompleteProps,
    AutocompleteRenderInputParams,
    ChipTypeMap,
} from '@mui/material';
import {
    Autocomplete,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { debounce, identity } from 'lodash';
import type { MouseEvent, ReactNode, SyntheticEvent } from 'react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useCustomTags } from '../AutoComplete/useCustomTags';
import { areOptionsOfEqualValue } from './areOptionsOfEqualValue';
import styles from './remoteAutoComplete.module.scss';
import { SelectFilterOption } from './SelectFilterOption';
import { renderGroup, useSelectItem } from './SelectItem';

export type LoadValuesHandler<T extends SelectFilterOption> = (params: {
    input: string;
    abortSignal: AbortSignal;
}) => Promise<T[]>;

type BaseProps<T extends SelectFilterOption> = {
    id?: string;
    disabled?: boolean;
    required?: boolean;
    label?: string;
    tabIndex?: number;
    wrapText?: boolean;
    /**
     * via valuesReference you can couple the currently loeaded values to a specific reference
     * when the reference changes, the values will be cleared
     **/
    valuesReference?: unknown;
    loadValues: LoadValuesHandler<T>;
    grouping?: boolean;
    noOptionsText?: React.ReactNode;
    inputEndAdornment?: ReactNode;
    inputPlaceholder?: string;
    clearOnBlur?: boolean;
    listboxProps?: {
        style?: React.CSSProperties;
    };
    open?: boolean;
    onOpen?: () => void;
    onClose?: (event: SyntheticEvent, reason: AutocompleteCloseReason) => void;
    initialValues?: T[];
    fallbackValues?: T[];
    renderInput?: (params: AutocompleteRenderInputParams) => React.JSX.Element;
    transparentBackground?: boolean;
    disableCloseOnSelect?: boolean;
    autoHighlight?: boolean;
    renderTags?: AutocompleteProps<
        T,
        true,
        false,
        false,
        ChipTypeMap['defaultComponent']
    >['renderTags'];
    inputValue?: string;
    onInputChange?: (event: SyntheticEvent, value: string) => void;
    endAdornmentText?: string;
    autoFocus?: boolean;
    onLoadingStateChange?: (isLoading: boolean) => void;
    onTagClick?: (ev: MouseEvent<HTMLElement>, value: T) => void;
    paperClassName?: string;
};

type SingleOptionProps<T extends SelectFilterOption> = BaseProps<T> & {
    type: 'single';
    value: T | null;
    onChange: (newValue: T | null, reason: AutocompleteChangeReason) => void;
    freeSolo?: false;
};
type SingleOptionPropsFreeSolo<T extends SelectFilterOption> = BaseProps<T> & {
    type: 'single';
    value: T | null;
    onChange: (
        newValue: T | string | null,
        reason: AutocompleteChangeReason,
    ) => void;
    freeSolo: true;
};

type MultipleOptionProps<T extends SelectFilterOption> = BaseProps<T> & {
    type: 'multiple';
    value: T[];
    onChange: (newValue: T[], reason: AutocompleteChangeReason) => void;
    freeSolo?: false;
};
type MultipleOptionPropsFreeSolo<T extends SelectFilterOption> =
    BaseProps<T> & {
        type: 'multiple';
        value: T[];
        onChange: (newValue: T[], reason: AutocompleteChangeReason) => void;
        freeSolo: true;
    };

type Props<T extends SelectFilterOption> =
    | SingleOptionProps<T>
    | SingleOptionPropsFreeSolo<T>
    | MultipleOptionProps<T>
    | MultipleOptionPropsFreeSolo<T>;

const getOptionLabel = (option: string | SelectFilterOption) => {
    if (typeof option === 'string') {
        return option;
    }
    return option?.label ?? '';
};

const groupBy = (option: SelectFilterOption) => option.group ?? '';

const PrivateRemoteAutoComplete = <T extends SelectFilterOption>({
    type,
    id,
    value,
    disabled,
    onChange,
    label,
    required,
    loadValues,
    tabIndex,
    wrapText,
    valuesReference,
    grouping,
    noOptionsText,
    freeSolo,
    inputEndAdornment,
    inputPlaceholder,
    clearOnBlur,
    listboxProps,
    onClose,
    onOpen,
    open,
    fallbackValues,
    initialValues,
    renderInput,
    transparentBackground,
    disableCloseOnSelect,
    autoHighlight,
    onInputChange,
    inputValue,
    endAdornmentText,
    renderTags,
    autoFocus,
    onLoadingStateChange,
    onTagClick,
    paperClassName,
}: Props<T>) => {
    const [options, setOptions] = useState<T[]>(initialValues ?? []);
    const [isLoading, setIsLoading] = useState(false);
    const { renderTags: renderDefaultTags } = useCustomTags<T, true>({
        onTagClick,
    });

    useEffect(() => {
        onLoadingStateChange?.(isLoading);
    }, [isLoading, onLoadingStateChange]);

    const abortControllerRef = useRef<AbortController | null>(null);

    const { renderSelectItem } = useSelectItem({ wrapText });

    // biome-ignore lint/correctness/useExhaustiveDependencies: unknown effect of fix
    useEffect(
        () => setOptions(fallbackValues ?? []),
        [fallbackValues, valuesReference],
    );

    const handleChange = useCallback(
        (
            _ev: SyntheticEvent,
            value: string | T | (string | T)[] | null,
            reason: AutocompleteChangeReason,
        ) => {
            if (type === 'multiple' && Array.isArray(value)) {
                const ops = value.reduce<T[]>((list, current) => {
                    if (typeof current === 'string') {
                        const element = options.find(
                            o =>
                                o.value.toLowerCase() ===
                                current.trim().toLowerCase(),
                        );
                        if (element) {
                            return list.concat(element);
                        }
                        return list;
                    }
                    return list.concat(current);
                }, []);

                onChange(ops, reason);
                if (ops.length === 0 && fallbackValues) {
                    setOptions(fallbackValues);
                }
            } else if (type === 'single' && !Array.isArray(value)) {
                if (typeof value === 'string') {
                    const element = options.find(
                        o =>
                            o.value.toLowerCase() ===
                            value.trim().toLowerCase(),
                    );
                    if (freeSolo) {
                        onChange(element ?? value, reason);
                    } else {
                        onChange(element ?? null, reason);
                    }
                } else {
                    onChange(value, reason);
                }
            }
        },
        [type, onChange, fallbackValues, options, freeSolo],
    );

    const fetchValues = useMemo(() => {
        const debouncedFn = debounce((input: string) => {
            abortControllerRef.current = new AbortController();
            setIsLoading(true);
            void loadValues({
                input,
                abortSignal: abortControllerRef.current.signal,
            })
                .then(setOptions)
                .catch(_err => {
                    // ignore
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }, 200);

        return (input: string) => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (input.length >= 2) {
                debouncedFn(input);
            } else {
                debouncedFn.flush();
                setOptions([]);
            }
        };
    }, [loadValues]);

    const handleTyping = useCallback(
        (
            ev: SyntheticEvent,
            input: string,
            reason: 'input' | 'clear' | 'reset',
        ) => {
            onInputChange?.(ev, input);
            if (reason === 'input') {
                fetchValues(input);
            } else if (reason === 'clear') {
                setOptions(fallbackValues ?? []);
            }
        },
        [fallbackValues, fetchValues, onInputChange],
    );

    const renderDefaultInput = useCallback(
        (params: AutocompleteRenderInputParams) => (
            <TextField
                {...params}
                required={required}
                label={label}
                size="small"
                inputProps={{
                    ...params.inputProps,
                    tabIndex,
                    placeholder: inputPlaceholder,
                    autoFocus,
                }}
                InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                        <Stack direction="row" alignItems="center" gap={1}>
                            {endAdornmentText && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    style={{ lineHeight: 1 }}
                                >
                                    {endAdornmentText}
                                </Typography>
                            )}
                            <CircularProgress
                                color="inherit"
                                size={20}
                                style={{
                                    visibility: isLoading
                                        ? 'visible'
                                        : 'hidden',
                                }}
                            />
                            {inputEndAdornment}
                            {params.InputProps.endAdornment}
                        </Stack>
                    ),
                }}
            />
        ),
        [
            required,
            label,
            tabIndex,
            isLoading,
            inputEndAdornment,
            inputPlaceholder,
            endAdornmentText,
            autoFocus,
        ],
    );

    const isMultiple = type === 'multiple';
    return (
        <Autocomplete
            className={
                transparentBackground
                    ? ''
                    : styles.autoCompleteWithDefaultBackground
            }
            // @ts-expect-error-next-line dynamic freeSolo options does not work with the Autocomplete generic
            multiple={isMultiple}
            disablePortal
            id={id}
            options={options}
            filterOptions={identity /* no filtering, we do it on the server */}
            fullWidth
            size="small"
            // @ts-expect-error-next-line dynamic freeSolo options does not work with the Autocomplete generic
            value={value}
            disabled={disabled}
            onInputChange={handleTyping}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={areOptionsOfEqualValue}
            loading={isLoading}
            disableCloseOnSelect={
                typeof disableCloseOnSelect === 'boolean'
                    ? disableCloseOnSelect
                    : isMultiple
            }
            renderOption={renderSelectItem}
            groupBy={grouping ? groupBy : undefined}
            renderGroup={renderGroup}
            renderInput={renderInput ?? renderDefaultInput}
            onChange={
                handleChange as (
                    ev: SyntheticEvent,
                    value:
                        | string
                        | SelectFilterOption
                        | (string | SelectFilterOption)[]
                        | null,
                    reason: AutocompleteChangeReason,
                ) => void /** necessary to cast, because MUI Autocomplete has issues dealing with our single / multiple values union type */
            }
            noOptionsText={noOptionsText}
            // @ts-expect-error-next-line dynamic freeSolo options does not work with the Autocomplete generic
            freeSolo={freeSolo}
            clearOnBlur={clearOnBlur}
            ListboxProps={{ style: listboxProps?.style }}
            open={open === undefined ? undefined : options.length > 0 && open}
            onOpen={onOpen}
            onClose={onClose}
            forcePopupIcon
            autoHighlight={autoHighlight}
            renderTags={renderTags ?? renderDefaultTags}
            inputValue={inputValue}
            slotProps={{
                paper: {
                    className: paperClassName,
                    elevation: 10,
                    style: {
                        borderRadius: 4,
                        marginTop: 1,
                    },
                },
            }}
        />
    );
};

const MemoizedRemoteAutoComplete = React.memo(PrivateRemoteAutoComplete);
MemoizedRemoteAutoComplete.displayName = 'RemoteAutoComplete';
export const RemoteAutoComplete =
    MemoizedRemoteAutoComplete as typeof PrivateRemoteAutoComplete;
